<template>
  <div class="d-flex align-items-center" style="min-height: 100vh">
    <div class="col-sm-8 col-md-6 col-lg-4 mx-auto" style="min-width: 300px">
      <div class="text-center mt-5 mb-5">
        <b-img :src="logo" width="200" :alt="brand" />
      </div>

      <card
        :title="$t('authMsgs.resetPassword')"
        subtitle="Reset your account password"
        header-class="text-center"
        class="navbar-shadow"
      >
        <slot />

        <b-form @submit.prevent="onSubmit">
          <b-form-group label="New Password" label-for="password" label-class="form-label">
            <div class="position-relative">
              <fmv-input-group-merge
                id="password"
                v-model="form.newPassword"
                :state="errorState"
                placeholder="New Password"
                :type="showNewPass ? 'text' : 'password'"
                class="form-control-prepended"
                required
                prepend
              >
                <span class="fas fa-key" />
              </fmv-input-group-merge>
              <i
                class="fas clickable-item password-toggle-icon"
                :class="showNewPass ? 'fa-eye-slash' : 'fa-eye'"
                :title="showNewPass ? 'Hide password' : 'Show password'"
                @click.prevent="showNewPass = !showNewPass"
              />
            </div>
            <password-strength-meter :password="form.newPassword" />
          </b-form-group>

          <b-form-group label="Repeat Password" label-for="password" label-class="form-label">
            <div class="position-relative">
              <fmv-input-group-merge
                id="password"
                v-model="form.repeatPassword"
                :state="errorState"
                placeholder="Repeat Password"
                :type="showRepeatPass ? 'text' : 'password'"
                class="form-control-prepended"
                required
                prepend
              >
                <span class="fas fa-key" />
              </fmv-input-group-merge>
              <i
                class="fas clickable-item password-toggle-icon"
                :class="showRepeatPass ? 'fa-eye-slash' : 'fa-eye'"
                :title="showRepeatPass ? 'Hide password' : 'Show password'"
                @click.prevent="showRepeatPass = !showRepeatPass"
              />
            </div>
          </b-form-group>

          <div v-if="error" class="alert alert-danger mt-3" role="alert">
            {{ $t(`passValidationMsgs.${errorCode}`) }}
          </div>

          <div class="form-group">
            <b-btn type="submit" variant="primary" block :disabled="isLoading" class="btn-normal">
              <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
              <span v-else>{{ $t('authMsgs.resetPassword') }}</span>
            </b-btn>
          </div>

          <div class="text-center">
            {{ $t('authMsgs.rememberPass') }}?
            <component
              :is="linkComponent"
              :to="routeTo(routes.login)"
              class="text-black-70"
              style="text-decoration: underline"
              v-text="$t('authMsgs.login')"
            />
          </div>
        </b-form>
      </card>
    </div>
  </div>
</template>


<script>
import Card from '@/components/Ui/Card.vue';
import Page from '@/components/Page';
import { mapActions } from 'vuex';
import { FmvAvatar } from 'fmv-avatar';
import { FmvInputGroupMerge } from 'fmv-input-group-merge';

import {
  PASSWORD_VALIDATIONS,
  PASSWORD_ALL_NUMERIC_REGEX,
  PASSWORD_COMPLEXITY_REGEX,
  PASSWORD_MIN_LENGTH,
  FORGOT_PASS_TOAST_OPTIONS,
} from '../../common/constants';
import PasswordStrengthMeter from '@/components/Ui/PasswordStrengthMeter.vue';
import logo from '@/assets/images/logos/transition_enroll_tp.png';

export default {
  components: {
    Card,
    FmvAvatar,
    FmvInputGroupMerge,
    PasswordStrengthMeter,
  },
  extends: Page,
  layout: 'blank',
  props: {
    brand: { type: String, default: () => 'Transition Enroll' },
    logo: { type: String, default: () => logo },
    routeLogo: { type: [String, Object] },
    routeLogin: { type: [String, Object] },
    errors: { type: Object, default: () => {} },
    linkComponent: { type: [String, Object], default: () => 'b-link' },
  },

  data() {
    return {
      title: 'Reset Password',
      form: {
        newPassword: null,
        repeatPassword: null,
      },
      showNewPass: false,
      showRepeatPass: false,
      error: false,
      errorCode: null,
      isLoading: false,
    };
  },

  computed: {
    errorState() {
      return this.error ? false : undefined;
    },
  },

  mounted() {
    if (!this.isLoggedIn) {
      this.setSettings({ layout: { layout: 'blank' } });
    }
  },

  watch: {},

  methods: {
    ...mapActions('settings', ['setSettings']),
    ...mapActions('auth', ['resetPasswordConfirm', 'updateResetPasswordTokenInvalid']),

    getErrors(key) {
      if (this.errors && this.errors[key] !== undefined) {
        return this.errors[key];
      }
    },
    async onSubmit() {
      this.isLoading = true;

      if (this.form.newPassword !== this.form.repeatPassword) {
        this.errorCode = PASSWORD_VALIDATIONS.PASSWORDS_NOT_MATCH;
        this.error = true;
      } else if (this.form.newPassword.length < PASSWORD_MIN_LENGTH) {
        this.errorCode = PASSWORD_VALIDATIONS.PASSWORDS_MIN_LENGTH;
        this.error = true;
      } else if (PASSWORD_ALL_NUMERIC_REGEX.test(this.form.newPassword)) {
        this.errorCode = PASSWORD_VALIDATIONS.PASSWORDS_ALL_NUMERIC;
        this.error = true;
      } else if (!PASSWORD_COMPLEXITY_REGEX.test(this.form.newPassword)) {
        this.errorCode = PASSWORD_VALIDATIONS.PASSWORDS_NO_COMPLEXITY;
        this.error = true;
      } else {
        try {
          const token = encodeURIComponent(this.$route.query.token);

          await this.resetPasswordConfirm({
            password: this.form.newPassword,
            token: token,
          });
          this.updateResetPasswordTokenInvalid(FORGOT_PASS_TOAST_OPTIONS.RESET_SUCCESSFUL);
          this.loading = false;
          this.$router.push({ name: 'login' });
        } catch (_e) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      }

      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.password-toggle-icon {
  position: absolute;
  top: 50%;
  right: 0.85rem;
  transform: translateY(-50%);
  color: #868e96;
  z-index: 4;
}
</style>
